import BaseDialog from 'components/dialog'
import ButtonBase from 'components/partials/button'
import InputBase from 'components/partials/inputBase'
import { notify } from 'components/toastify'
import { customStyles } from 'constants/customStyles'
import { STATUS } from 'constants/messages'
import { onHandleCopyItem } from 'helpers/functionUtils'
import axiosClient from 'network/axiosClient'
import React, { useEffect, useState } from 'react'
import ReactLoading from 'react-loading'
import { ClipLoader } from 'react-spinners'
import { useDebounceValue } from 'usehooks-ts'
import { emailPattern2 } from 'utils/regex'
import { IError } from '../../interface'

interface Props {
  isOpenPreview: boolean
  htmlString: string
  handleClosePreviewDialog: () => void
  isLoadingData: boolean
  sendingEmailSubject: string
  index?: number | null
}

const iframeDialogStyle: ReactModal.Styles = {
  content: { ...customStyles.content, maxHeight: '700px', maxWidth: '900px' },
}

const PreviewDialog: React.FC<Props> = ({
  isOpenPreview,
  htmlString,
  // config,
  handleClosePreviewDialog,
  isLoadingData,
  sendingEmailSubject,
  index,
}) => {
  const [isSending, setIsSending] = useState(false)
  const [emailAddress, setEmailAddress] = useState<string | null>(null)
  const [debouncedEmail] = useDebounceValue(emailAddress, 500)
  const [error, setError] = useState<IError>({
    isError: false,
    message: null,
  })

  const onTestSendBtn = async () => {
    if (!sendingEmailSubject || htmlString.length === 0) return

    setIsSending(true)

    const response = await axiosClient.post('/agencyGroup/test/send', {
      to: debouncedEmail,
      subject: sendingEmailSubject,
      html: htmlString,
    })

    if (response.status !== 200) {
      notify(response.data.message ?? 'Something wrong', STATUS.ERROR)
      setIsSending(true)
      return
    }

    notify('Sent email, please check your mailbox', STATUS.SUCCESS)
    setIsSending(false)
  }

  const onClickCopyHtmlBtn = async () => {
    await onHandleCopyItem(htmlString, 'Copied Html', 'Something wrong')
  }

  const onChangeEmailAddress = (value: string) => {
    if (value) {
      setEmailAddress(value)
    } else {
      setEmailAddress(null)
    }
  }

  const validateEmail = (value: string) => {
    const { value: emailRegex, message } = emailPattern2
    const isValid = emailRegex.test(value)
    return {
      isValid,
      message: !isValid ? message : null,
    }
  }

  useEffect(() => {
    if (debouncedEmail) {
      const { isValid, message } = validateEmail(debouncedEmail)
      setError({
        isError: !isValid,
        message,
      })
    } else {
      setError({
        isError: false,
        message: null,
      })
    }
  }, [debouncedEmail])

  useEffect(() => {
    setError({ isError: false, message: null })
    setIsSending(false)
    setEmailAddress(null)
  }, [])

  return (
    <BaseDialog modalIsOpen={isOpenPreview} customStyles={iframeDialogStyle}>
      <div className="space-y-2">
        {isLoadingData ? (
          <div className="flex justify-center p-5">
            <ReactLoading type="spin" color="#A989D8" height={30} width={30} />
          </div>
        ) : (
          <iframe
            // onLoad={onLoadPreview}
            className="w-[850px] h-[500px]"
            title="clicking-preview"
            srcDoc={htmlString}
          ></iframe>
        )}

        <div className="flex space-x-1">
          <div className="w-[50%]">
            <div>
              <InputBase
                name={`email-test-send-input-${index}`}
                className="w-full"
                placeholder="Enter Email Address to test"
                handleChange={onChangeEmailAddress}
              />
              <span className="text-red-500">{error.message}</span>
            </div>
          </div>
          <div className="w-[50%] flex space-x-1 justify-end">
            {isSending ? (
              <div className="flex flex items-center">
                <ClipLoader color={'#523280'} size={40} />
              </div>
            ) : (
              <ButtonBase
                onClick={onTestSendBtn}
                disabled={!debouncedEmail || error.isError}
                bgDisabled="bg-violet-600"
              >
                Test Send Email
              </ButtonBase>
            )}

            <ButtonBase onClick={onClickCopyHtmlBtn} disabled={isLoadingData}>
              Copy HTML
            </ButtonBase>
            <ButtonBase onClick={handleClosePreviewDialog}>Close</ButtonBase>
          </div>
        </div>
      </div>
    </BaseDialog>
  )
}

export default PreviewDialog
